import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/ui';
import { ArrowRight, Bell, CalendarIcon, ChevronDown, CircleHelp, Database, DollarSign, Info, Loader2, MessageCircle, Phone, RefreshCw, Sparkles, UserCheck, XCircle } from '@/components/ui/icons/Icons';
import { useI18n } from '@/context/I18nProvider';
import { cn } from '@/lib/utils';
import { ApiClient } from '@/services/api-client';
import { GetLeadByIDResponse } from '@/services/types/global.types';
import { ConversationSummary, RecommendedAction, RecommendedActionType } from '@/services/types/Lead.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
const INTEREST_STYLES: Record<ConversationSummary['InterestLevel'], string> = {
  high: 'bg-green-50 text-green-700 border-green-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-red-50 text-red-600 border-red-200',
  unknown: 'bg-muted text-muted-foreground border-border'
};
const ACTION_ICONS: Record<RecommendedActionType, typeof Phone> = {
  call_back: Phone,
  follow_up: MessageCircle,
  send_booking_link: CalendarIcon,
  send_pricing: DollarSign,
  send_information: Info,
  answer_question: CircleHelp,
  update_crm: Database,
  set_reminder: Bell,
  mark_qualified: UserCheck,
  mark_not_qualified: XCircle,
  other: ArrowRight
};
const PRIORITY_DOT: Record<RecommendedAction['Priority'], string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-gray-300'
};
type ConversationSummaryPanelProps = {
  leadId: string;
  summary?: ConversationSummary | null;
  /** Messages currently in the conversation, to flag a summary that predates the latest ones. */
  messageCount: number;
  isMobile?: boolean;
};

/**
 * AI recap of the conversation for the salesperson taking over. Only generated
 * when the user clicks the button — never on each message — and stored on the
 * conversation so everyone on the workspace sees the same one.
 */
const ConversationSummaryPanel = t0 => {
  const $ = _c(59);
  const {
    leadId,
    summary,
    messageCount,
    isMobile
  } = t0;
  const {
    t,
    locale
  } = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== leadId) {
    t2 = [leadId];
    $[1] = leadId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== leadId || $[4] !== locale) {
    t3 = () => ApiClient.lead.generateConversationSummary({
      leadId,
      language: locale
    });
    $[3] = leadId;
    $[4] = locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== leadId || $[7] !== queryClient) {
    t4 = res => {
      queryClient.setQueryData(["lead.getLead", {
        id: leadId
      }], old => {
        if (!old?.data) {
          return old;
        }
        return {
          ...old,
          data: {
            ...old.data,
            Summary: res.data
          }
        };
      });
      setOpen(true);
    };
    $[6] = leadId;
    $[7] = queryClient;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = () => toast.error(t("conversations.summary.error"));
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = {
      mutationFn: t3,
      onSuccess: t4,
      onError: t5
    };
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const mGenerate = useMutation(t6);
  const newMessages = summary ? Math.max(0, messageCount - summary.MessageCount) : 0;
  let t7;
  if ($[15] !== isMobile || $[16] !== locale || $[17] !== mGenerate || $[18] !== messageCount || $[19] !== newMessages || $[20] !== open || $[21] !== summary || $[22] !== t) {
    const generatedAt = summary ? new Date(summary.GeneratedAt).toLocaleString(locale, {
      dateStyle: "short",
      timeStyle: "short"
    }) : "";
    let t8;
    if ($[24] !== mGenerate) {
      t8 = () => mGenerate.mutate();
      $[24] = mGenerate;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    const t9 = mGenerate.isPending || messageCount === 0;
    let t10;
    if ($[26] !== mGenerate.isPending || $[27] !== summary) {
      t10 = mGenerate.isPending ? <Loader2 className="size-3.5 animate-spin" /> : summary ? <RefreshCw className="size-3.5" /> : <Sparkles className="size-3.5" />;
      $[26] = mGenerate.isPending;
      $[27] = summary;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    let t11;
    if ($[29] !== mGenerate.isPending || $[30] !== summary || $[31] !== t) {
      t11 = mGenerate.isPending ? t("conversations.summary.generating") : summary ? t("conversations.summary.regenerate") : t("conversations.summary.generate");
      $[29] = mGenerate.isPending;
      $[30] = summary;
      $[31] = t;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    let t12;
    if ($[33] !== t10 || $[34] !== t11 || $[35] !== t8 || $[36] !== t9) {
      t12 = <Button variant="outline" size="sm" className="h-7 shrink-0 gap-1.5 rounded-md border-border px-2.5 shadow-none" onClick={t8} disabled={t9}>{t10}{t11}</Button>;
      $[33] = t10;
      $[34] = t11;
      $[35] = t8;
      $[36] = t9;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    const generateButton = t12;
    const listSections = summary ? [{
      key: "needs",
      label: t("conversations.summary.needs"),
      items: summary.Needs
    }, {
      key: "qualification",
      label: t("conversations.summary.qualificationAnswers"),
      items: summary.QualificationAnswers
    }, {
      key: "objections",
      label: t("conversations.summary.objections"),
      items: summary.Objections
    }, {
      key: "questions",
      label: t("conversations.summary.openQuestions"),
      items: summary.OpenQuestions
    }, {
      key: "notes",
      label: t("conversations.summary.importantNotes"),
      items: summary.ImportantNotes
    }].filter(_temp) : [];
    const facts = summary ? [{
      key: "status",
      label: t("conversations.summary.currentStatus"),
      value: summary.CurrentStatus
    }, {
      key: "next",
      label: t("conversations.summary.nextStep"),
      value: summary.NextStep
    }, {
      key: "budget",
      label: t("conversations.summary.budget"),
      value: summary.Budget
    }, {
      key: "timeline",
      label: t("conversations.summary.timeline"),
      value: summary.Timeline
    }].filter(_temp2) : [];
    const t13 = isMobile ? "px-3" : "pl-4 pr-6";
    let t14;
    if ($[38] !== t13) {
      t14 = cn("flex items-center justify-between gap-3 py-2", t13);
      $[38] = t13;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    let t15;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = () => setOpen(_temp3);
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    const t16 = !summary;
    let t17;
    if ($[41] !== open || $[42] !== t) {
      t17 = open ? t("conversations.summary.hide") : t("conversations.summary.show");
      $[41] = open;
      $[42] = t;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    let t18;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Sparkles className="size-4 shrink-0 text-muted-foreground" />;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    let t19;
    if ($[45] !== t) {
      t19 = t("conversations.summary.title");
      $[45] = t;
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    let t20;
    if ($[47] !== t19) {
      t20 = <span className="shrink-0 text-sm font-medium text-text-primary">{t19}</span>;
      $[47] = t19;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    const t21 = summary ? <><span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium", INTEREST_STYLES[summary.InterestLevel] ?? INTEREST_STYLES.unknown)}>{t(`conversations.summary.interest.${summary.InterestLevel ?? "unknown"}`)}</span><span className="hidden truncate text-xs text-muted-foreground sm:inline">{t("conversations.summary.generatedAt", {
          date: generatedAt
        })}{newMessages > 0 ? <span className="text-orange-600">{" \xB7 "}{t("conversations.summary.outdated", {
            count: newMessages
          })}</span> : null}</span><ChevronDown className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} /></> : <span className="hidden truncate text-xs text-muted-foreground md:inline">{t("conversations.summary.empty")}</span>;
    let t22;
    if ($[49] !== open || $[50] !== t16 || $[51] !== t17 || $[52] !== t20 || $[53] !== t21) {
      t22 = <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-default" onClick={t15} disabled={t16} aria-expanded={open} aria-label={t17}>{t18}{t20}{t21}</button>;
      $[49] = open;
      $[50] = t16;
      $[51] = t17;
      $[52] = t20;
      $[53] = t21;
      $[54] = t22;
    } else {
      t22 = $[54];
    }
    let t23;
    if ($[55] !== generateButton || $[56] !== t14 || $[57] !== t22) {
      t23 = <div className={t14}>{t22}{generateButton}</div>;
      $[55] = generateButton;
      $[56] = t14;
      $[57] = t22;
      $[58] = t23;
    } else {
      t23 = $[58];
    }
    t7 = <section className="border-b border-border">{t23}{summary && open ? <div className={cn("max-h-[40vh] space-y-3 overflow-y-auto pb-3 text-sm", isMobile ? "px-3" : "pl-4 pr-6")}><p className="leading-relaxed text-text-primary">{summary.Overview}</p>{summary.RecommendedActions?.length ? <div><p className="text-xs font-medium text-muted-foreground">{t("conversations.summary.recommendedActions")}</p><ul className="mt-1.5 space-y-1.5">{summary.RecommendedActions.map((action, index) => {
              const Icon = ACTION_ICONS[action.Type] ?? ArrowRight;
              return <li key={index} className="flex items-start gap-2.5 rounded-lg border border-border bg-background px-3 py-2"><Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" /><div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-x-2 gap-y-0.5"><span className={cn("size-1.5 shrink-0 rounded-full", PRIORITY_DOT[action.Priority] ?? PRIORITY_DOT.medium)} title={t(`conversations.summary.priority.${action.Priority ?? "medium"}`)} /><span className="font-medium text-text-primary">{action.Title}</span>{action.Timing ? <span className="text-xs text-muted-foreground">· {action.Timing}</span> : null}</div>{action.Reason ? <p className="text-xs text-muted-foreground">{action.Reason}</p> : null}</div></li>;
            })}</ul></div> : null}{facts.length ? <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">{facts.map(_temp4)}</dl> : null}{listSections.length ? <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">{listSections.map(_temp6)}</div> : null}</div> : null}</section>;
    $[15] = isMobile;
    $[16] = locale;
    $[17] = mGenerate;
    $[18] = messageCount;
    $[19] = newMessages;
    $[20] = open;
    $[21] = summary;
    $[22] = t;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
};
export { ConversationSummaryPanel };
function _temp(section) {
  return section.items?.length;
}
function _temp2(fact) {
  return fact.value;
}
function _temp3(value) {
  return !value;
}
function _temp4(fact_0) {
  return <div key={fact_0.key} className="min-w-0"><dt className="text-xs font-medium text-muted-foreground">{fact_0.label}</dt><dd className="text-text-primary">{fact_0.value}</dd></div>;
}
function _temp5(item, index_0) {
  return <li key={index_0}>{item}</li>;
}
function _temp6(section_0) {
  return <div key={section_0.key} className="min-w-0"><p className="text-xs font-medium text-muted-foreground">{section_0.label}</p><ul className="mt-1 list-disc space-y-0.5 pl-4 text-text-primary">{section_0.items.map(_temp5)}</ul></div>;
}